import { DesktopTrust } from 'components/pdp/desktop-trust'
import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Typo from 'constants/typography'
import ProductBanner from 'components/pdp/product-banner/ProductBanner'
import VoucherPDP from 'components/pdp/carousel-images/voucher/VoucherPDP'
import WishlistCTA from 'components/pdp/interactions/wishlist-cta'
import VoucherApiHandler from 'lib/api/voucher'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import ProductVoucherModal from 'components/shared/product-voucher-modal/ProductVoucherModal'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { withI18next } from 'lib/i18n/withI18next'

function getPercentDiscount(prices, voucher) {
  if (voucher) {
    return (
      <div className="pdp__price-reduction-label">
        <span className={Typo.caption}>
          -{voucher.discount_percentage_formatted}
        </span>
      </div>
    )
  }

  if (prices?.reduction?.amount && prices?.reduction?.type === 'percentage') {
    let discountAmount = (prices?.reduction?.amount || 0) * 100
    discountAmount = Math.ceil(discountAmount)

    return (
      <div className="pdp__price-reduction-label">
        <span className={Typo.caption}>{-discountAmount} %</span>
      </div>
    )
  }

  return null
}

const ProductPriceName = ({
  t,
  language,
  name,
  refId,
  prices,
  isPhone,
  refType,
  product,
  brandURL,
  productId,
  manufacturer,
  isComingSoon,
  onClickReview,
  comingSoonText,
  setIsSnackBarOpen,
  isShownVoucherAlert,
  avg_overall_product_rating,
  isDeleteWishlistProductTimeOut,
}) => {
  const [productVoucher, setProductVoucher] = useState(null)
  const hasDiscountedPrice =
    productVoucher?.voucher ||
    (prices?.discounted_price_formatted &&
      prices?.discounted_price_formatted !== prices?.original_price_formatted)
  const discountBadge = getPercentDiscount(prices, productVoucher?.voucher)
  const discountedPrice =
    productVoucher?.voucher?.estimated_final_price_formatted ||
    prices?.discounted_price_formatted

  useEffect(() => {
    const getProductVoucher = async () => {
      try {
        const res = await VoucherApiHandler.getProductsVouchers([productId])
        if (res) {
          setProductVoucher(res?.[0])
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error(error)
      }
    }

    if (productId) {
      getProductVoucher()
    }
  }, [productId])

  return (
    <div
      className={
        hasDiscountedPrice
          ? 'pdp__price-name__container__with-discount-price'
          : 'pdp__price-name__container'
      }
      id="pdp_price_name"
    >
      {!isPhone && (
        <ProductBanner
          is_coming_soon={isComingSoon}
          coming_soon_text={comingSoonText}
          is_flash_sale={prices?.is_flash_sale}
          flash_sale_seconds_left={prices?.flash_sale_seconds_left}
        />
      )}
      <h1 className="pdp__name-container">
        <div className="pdp__product-manufacturer">
          {manufacturer?.name && brandURL && (
            <Link {...brandURL}>
              <a
                role="link"
                tabIndex="0"
                onKeyDown={(e) => {
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
                className="pdp__product-manufacturer__name"
              >
                <span className={clsx(Typo.subtitle2)}>
                  {manufacturer.name}
                </span>
              </a>
            </Link>
          )}
          {isShownVoucherAlert && <VoucherPDP productId={productId} />}
        </div>
        <div className="pdp__name-self">
          <span className={clsx(Typo.body2)} data-test="pdp__product-name">
            {name}
          </span>
          <WishlistCTA
            isOnWishlist={product.is_on_wishlist}
            isDeleteWishlistProductTimeOut={isDeleteWishlistProductTimeOut}
            product={product}
            refType={refType}
            refId={refId}
            setIsSnackBarOpen={setIsSnackBarOpen}
          />
        </div>
      </h1>
      <DesktopTrust
        language={language}
        rating={avg_overall_product_rating}
        salesCount={product.sales_count}
        onReviews={onClickReview}
        t={t}
      />
      <div className="pdp__price-container-wrapper">
        <div className="pdp__price-container">
          {hasDiscountedPrice ? (
            <React.Fragment>
              <span className={clsx('pdp__discounted-price', Typo.title3)}>
                {discountedPrice}
              </span>
              {Boolean(productVoucher?.voucher) && (
                <ProductVoucherModal
                  isPhone={isPhone}
                  isShowPriceBreakDown
                  productVoucher={productVoucher}
                  renderTrigger={({ openModal }) => (
                    <span
                      tabIndex={0}
                      role="button"
                      className={clsx(
                        'pdp__discounted-price__with-voucher',
                        Typo.price2,
                      )}
                      onClick={openModal}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openModal()
                        }
                      }}
                    >
                      {`${productVoucher?.voucher?.discount_text} `}
                      <Icon
                        src={ICONS.productVoucher}
                        size={ICON_SIZE.smallMedium}
                      />
                    </span>
                  )}
                />
              )}
              <span
                className={clsx(
                  'pdp__original-price-line-through',
                  Typo.price2,
                )}
              >
                {prices.original_price_formatted}
              </span>
              {discountBadge}
            </React.Fragment>
          ) : (
            <span className={clsx('pdp__original-price', Typo.price1)}>
              {prices.original_price_formatted}
            </span>
          )}
        </div>
        {productVoucher?.is_best_price_guaranteed && (
          <ProductVoucherModal
            isPhone={isPhone}
            isShowPriceBreakDown={false}
            renderTrigger={({ openModal }) => (
              <span
                tabIndex={0}
                role="button"
                className={clsx(
                  'pdp__price-container-wrapper__best-price-guranteed',
                  Typo.overline,
                )}
                onClick={openModal}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openModal()
                  }
                }}
              >
                {hasLocizeTranslation(
                  t,
                  'BEST_PRICE_GUARANTEED',
                  'Best Price Guaranteed',
                )}
                <Icon src={ICONS.question} size={ICON_SIZE.small} />
              </span>
            )}
          />
        )}
      </div>
    </div>
  )
}

ProductPriceName.defaultProps = {
  language: 'en',
  isPhone: false,
  comingSoonText: '',
  isComingSoon: false,
  isShownVoucherAlert: false,
  avg_overall_product_rating: null,
}

ProductPriceName.propTypes = {
  language: PropTypes.string,
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool,
  isComingSoon: PropTypes.bool,
  comingSoonText: PropTypes.string,
  name: PropTypes.string.isRequired,
  isShownVoucherAlert: PropTypes.bool,
  brandURL: PropTypes.shape({
    as: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }).isRequired,
  manufacturer: PropTypes.shape({
    name: PropTypes.string.isRequired,
    is_pomelo: PropTypes.bool.isRequired,
  }).isRequired,
  prices: PropTypes.shape({
    reduction: PropTypes.shape({
      type: PropTypes.string,
      amount: PropTypes.number,
    }),
    is_flash_sale: PropTypes.bool,
    flash_sale_seconds_left: PropTypes.number,
    discounted_price_formatted: PropTypes.string,
    original_price_formatted: PropTypes.string.isRequired,
  }).isRequired,
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  isDeleteWishlistProductTimeOut: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    sales_count: PropTypes.number,
    is_on_wishlist: PropTypes.bool.isRequired,
  }).isRequired,
  refType: PropTypes.string.isRequired,
  refId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  setIsSnackBarOpen: PropTypes.func.isRequired,
  onClickReview: PropTypes.func.isRequired,
  avg_overall_product_rating: PropTypes.number,
}

export default withI18next()(ProductPriceName)
